import { Formik } from 'formik'
import { forwardRef, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector, useDispatch } from 'react-redux'
import styled from 'styled-components'
import { Button } from '@/components/button'
import CloseButton from '@/components/close-button'
import { Autosave } from '@/components/form'
import { toast } from '@/components/toast-notifications'
import { select } from '@/store'
import { colors, breakpoints, fontSizes, fontWeights } from '@/styles'
import ProfileDiscovery from './discovery'
import AllowCall from './allow-call'

const Wrapper = styled.div`
  background: ${colors.white};
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;

  box-shadow: 0 -2px 12px 0 rgba(0, 0, 0, 0.8);
  @media screen and (max-height: 800px) {
    box-shadow: 0 -2px 4px 0 rgba(0, 0, 0, 0.3);
  }
  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    box-shadow: 0 -2px 4px 0 rgba(0, 0, 0, 0.3);
  }
`

const InnerWrapper = styled.div`
  width: 100%;

  position: relative;
  display: flex;
  justify-content: center;
`

const DescriptionWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0;
  max-width: 1030px;
  width: 100%;
  padding: 50px 25px;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    padding: 20px 25px;
  }
`

const InfoText = styled.p`
  font-size: ${fontSizes.regular};
  margin: 0;
  padding-right: 15px;
`

const FlexButton = styled(Button)`
  white-space: pre;
`

const DescriptionTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`

const Heading = styled.p`
  color: ${colors.regular};
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.medium};
  line-height: 24px;
  margin: 0 0 15px;
  ${props => props.bottom && `margin-bottom: ${props.bottom}px;`}

  ${props =>
    props.hideInMobile &&
    `

  @media only screen and (max-width: ${breakpoints.phoneMax}) {
    display: none;
  }
  `}
`

const PrivacySwitchWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 960px;
  padding: 50px 25px;
`

//sorry
const ToggleGroup = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;

  .descriptionField {
    width: unset;
    max-width: unset;
    flex: 1;
  }

  .descriptionField:not(:last-child) {
    border-right: 1px solid ${colors.grey};
    padding-right: 80px;
  }

  .descriptionField:not(:first-child) {
    margin-left: 80px;
  }

  @media screen and (max-width: ${breakpoints.dashboardMobile}) {
    .descriptionField:not(:last-child) {
      padding-right: 40px;
    }

    .descriptionField:not(:first-child) {
      margin-left: 40px;
    }
  }

  @media screen and (max-width: 1000px) {
    flex-direction: column;
    align-items: center;

    .descriptionField {
      max-width: unset;
      width: 100%;
    }

    .descriptionField:not(:last-child) {
      border-right: none;
      padding-right: 0;
      margin-bottom: 36px;
    }

    .descriptionField:not(:first-child) {
      margin-left: 0;
    }
  }
`

const PrivacySwitches = ({ onClose }) => {
  const { t } = useTranslation()

  return (
    <PrivacySwitchWrapper>
      <CloseButton onClick={onClose} />
      <Heading bottom={34}>
        {t('r.settings.view.privacy_settings.title', 'Privacy Settings')}
      </Heading>
      <ToggleGroup>
        <ProfileDiscovery condensed />
        <AllowCall condensed />
      </ToggleGroup>
    </PrivacySwitchWrapper>
  )
}

const Description = ({ displaySwitches }) => {
  const { t } = useTranslation()
  const label = t(
    'r.settings.view.viewing_as_landlord.label',
    'This is how landlords see your profile'
  )
  return (
    <DescriptionWrapper>
      <DescriptionTextContainer>
        <Heading hideInMobile>
          {t('r.profile.view.viewing_as_landlord.title', 'Viewing your profile as landlord')}
        </Heading>
        <InfoText>{label}</InfoText>
      </DescriptionTextContainer>
      <FlexButton theme="secondary" onClick={displaySwitches}>
        {t('r.settings.view.profile_edit_privacy.button', 'Edit Privacy')}
      </FlexButton>
    </DescriptionWrapper>
  )
}

const Bar = forwardRef(({ className }, ref) => {
  const { t } = useTranslation()
  const settings = useSelector(select.profile.settings)
  const dispatch = useDispatch()
  const [areSwitchesDisplayed, setAreSwitchesDisplayed] = useState(false)
  const onSubmit = useCallback(
    async values => {
      const response = await dispatch.profile.updateSettings(values)
      if (!response?.ok) {
        toast(t('b.settings.view.settings.failed_save', 'Your settings could not be saved.'), {
          title: t('common.error', 'Error'),
          type: 'error'
        })
      }
    },
    [dispatch.profile, t]
  )
  return (
    <Wrapper ref={ref} className={className}>
      <InnerWrapper>
        <Formik initialValues={settings}>
          {({ values, dirty }) => (
            <Autosave values={values} onSave={dirty ? onSubmit : undefined}>
              {areSwitchesDisplayed ? (
                <PrivacySwitches onClose={() => setAreSwitchesDisplayed(false)} />
              ) : (
                <Description displaySwitches={() => setAreSwitchesDisplayed(true)} />
              )}
            </Autosave>
          )}
        </Formik>
      </InnerWrapper>
    </Wrapper>
  )
})

Bar.displayName = 'Bar'

export default Bar
